// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2)
// version  : 2.0
// updated  : 2026-06-20
// protected: NO
// changelog: v2.0 (P0-C Fase 2) - Work Order MULTI-MERCHANDIZE:
//            - tiap baris pilih Merchandize (dropdown Master, lintas region utk Produksi)
//            - Jenis Stok (JUAL/MoU) per-baris -> 1 WO bisa campur
//            - Size grid S..7XL (has_size_variants) atau Quantity total (tanpa size)
//            - Region Produksi (dari role) + Region Pemilik Merchandize (read-only)
//            - WO lama tetap terbaca (fallback 1 line). Label user-facing: "Merchandize".
// =============================================================================
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { StatusBadge, CategoryBadge } from "@/v2/components/statusMap";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Loader2, FileText, PackageCheck, Send, QrCode, Trash2, Info } from "lucide-react";import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { useV2LiveRefresh } from "@/v2/V2RealtimeContext";
import { openPdf } from "@/v2/components/helpers";
import { ListPager, loadStoredPageSize } from "@/v2/components/ListPager";

const PRODUKSI = ["ADMIN_PRODUKSI_SBY", "ADMIN_PRODUKSI_SMD"];
const KACAB = ["KACAB_SBY", "KACAB_SMD"];
const REVISABLE = ["Draft", "Ditolak", "Diajukan"];
const SIZES = ["S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL", "6XL", "7XL"];
const REGION_LABEL = { SBY: "Surabaya", SMD: "Samarinda", GLOBAL: "-" };
const PROD_REGION_BY_ROLE = { ADMIN_PRODUKSI_SBY: "SBY", ADMIN_PRODUKSI_SMD: "SMD" };

const fmtTs = (s) => {
  if (!s) return null;
  try { return new Date(s).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" }); }
  catch (e) { return s; }
};
const rid = () => Math.random().toString(36).slice(2, 9);
const newLine = () => ({ key: rid(), product_id: "", item_category: "Jual", sizes: {}, qtyNoSize: "" });

export default function WorkOrderPage() {
  const { me, api } = useV2Auth();
  const [wos, setWos] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  // Paginasi (Slice S-G) — reuse <ListPager> + loadStoredPageSize.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => loadStoredPageSize("work-orders"));
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const listTopRef = useRef(null);
  const pageRef = useRef(1);
  const [design, setDesign] = useState("");
  const [woOffline, setWoOffline] = useState("");
  const [search, setSearch] = useState("");
  const [lines, setLines] = useState([newLine()]);
  const [editId, setEditId] = useState(null);
  const [submitting, setSubmitting] = useState(false);  // C.1: form Buat/Revisi WO
  const [busyAct, setBusyAct] = useState(null);          // C.1: aksi baris (`${id}:${action}`)

  const canCreate = PRODUKSI.includes(me?.role);
  const canApprove = KACAB.includes(me?.role) || me?.role === "OWNER";
  const prodRegion = PROD_REGION_BY_ROLE[me?.role] || null;
  const productById = (id) => products.find((p) => p.id === id);

  const load = useCallback(async (p, size = pageSize) => {
    const target = p || pageRef.current || 1;
    setLoading(true);
    try {
      const r = await api(`/api/v2/work-orders?page=${target}&page_size=${size}`);
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setWos(d.items || []);
        setMeta({ total: d.total || 0, pages: d.pages || 1 });
        setPage(d.page || target); pageRef.current = d.page || target;
      } else setWos([]);
    } finally { setLoading(false); }
  }, [api, pageSize]);
  const goPage = (p) => {
    load(p);
    if (listTopRef.current) listTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const changePageSize = (s) => { setPageSize(s); load(1, s); };

  const loadProducts = useCallback(async () => {
    const r = await api("/api/v2/merchandise/products");
    if (r.ok) { const d = await r.json(); setProducts(d.items || []); }
  }, [api]);

  useEffect(() => { load(); }, [load]);
  useV2LiveRefresh(load);  // FASE 7B
  useEffect(() => { if (canCreate) loadProducts(); }, [canCreate, loadProducts]);

  // SLICE S-A (A-4): terima parameter ?product=<id> dari notifikasi stok -> PRA-PILIH produk
  // di baris pertama. TANPA param -> perilaku identik. Param tak dikenal -> diabaikan.
  // Tidak membuat dokumen apa pun; manusia tetap mengisi size & jumlah lalu menekan simpan.
  const [searchParams] = useSearchParams();
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    if (prefilled || !canCreate) return;
    const pid = searchParams.get("product");
    if (!pid || !products.length) return;
    if (!products.some((p) => p.id === pid)) { setPrefilled(true); return; }  // tak dikenal -> abaikan
    setLines((prev) => (prev.length === 1 && !prev[0].product_id
      ? [{ ...prev[0], product_id: pid }] : prev));
    setPrefilled(true);
  }, [searchParams, products, canCreate, prefilled]);

  const resetForm = () => { setEditId(null); setDesign(""); setWoOffline(""); setLines([newLine()]); };

  const startRevise = (w) => {
    setEditId(w.id);
    setDesign(w.design_name || "");
    setWoOffline(w.wo_no_offline || "");
    // Rekonstruksi builder: kelompokkan line lama per (product_id + item_category).
    const groups = {};
    (w.lines || []).forEach((l) => {
      const k = `${l.product_id}|${l.item_category || "Jual"}`;
      if (!groups[k]) groups[k] = { key: rid(), product_id: l.product_id || "", item_category: l.item_category || "Jual", sizes: {}, qtyNoSize: "" };
      const p = productById(l.product_id);
      if (p && !p.has_size_variants) groups[k].qtyNoSize = String((Number(groups[k].qtyNoSize) || 0) + Number(l.qty || 0));
      else groups[k].sizes[l.size] = String((Number(groups[k].sizes[l.size]) || 0) + Number(l.qty || 0));
    });
    const arr = Object.values(groups);
    setLines(arr.length ? arr : [newLine()]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const setLine = (key, patch) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const removeLine = (key) => setLines((ls) => (ls.length > 1 ? ls.filter((l) => l.key !== key) : ls));
  const setSizeQty = (key, sz, val) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, sizes: { ...l.sizes, [sz]: val } } : l)));

  const lineTotal = (l) => {
    const p = productById(l.product_id);
    if (p && !p.has_size_variants) return Number(l.qtyNoSize || 0);
    return SIZES.reduce((s, sz) => s + Number(l.sizes[sz] || 0), 0);
  };

  const buildPayloadLines = () => {
    const out = [];
    for (const l of lines) {
      if (!l.product_id) continue;
      const p = productById(l.product_id);
      const cat = l.item_category || "Jual";
      if (p && !p.has_size_variants) {
        const q = Number(l.qtyNoSize || 0);
        if (q > 0) out.push({ product_id: l.product_id, size: "-", qty: q, item_category: cat });
      } else {
        for (const sz of SIZES) {
          const q = Number(l.sizes[sz] || 0);
          if (q > 0) out.push({ product_id: l.product_id, size: sz, qty: q, item_category: cat });
        }
      }
    }
    return out;
  };

  const submitForm = async () => {
    const payloadLines = buildPayloadLines();
    if (!payloadLines.length) { toast.error("Pilih minimal 1 Merchandize dengan Quantity > 0"); return; }
    if (!woOffline.trim()) { toast.error("Nomor WO Offline wajib diisi"); return; }
    if (submitting) return;  // C.1: cegah klik ganda
    setSubmitting(true);
    try {
      const payload = { production_region: prodRegion, wo_no_offline: woOffline.trim(), design_name: design || null, lines: payloadLines };
      const url = editId ? `/api/v2/work-orders/${editId}/revise` : "/api/v2/work-orders";
      const r = await api(url, { method: "POST", body: JSON.stringify(payload) });
      if (r.ok) { toast.success(editId ? "Work Order direvisi" : "Work Order dibuat"); resetForm(); load(); }
      else toast.error((await r.json()).detail || "Gagal menyimpan Work Order");
    } finally {
      setSubmitting(false);
    }
  };

  const act = async (id, action) => {
    const key = `${id}:${action}`;
    if (busyAct) return;  // C.1: cegah aksi ganda/beruntun (satu aksi baris pada satu waktu)
    setBusyAct(key);
    try {
      const body = action === "reject" ? JSON.stringify({ reason: "Revisi" })
        : action === "cancel" ? JSON.stringify({ reason: "Dibatalkan pembuat" }) : undefined;
      const r = await api(`/api/v2/work-orders/${id}/${action}`, { method: "POST", body });
      if (r.ok) { toast.success(`Work Order: ${action}`); load(); }
      else toast.error((await r.json()).detail || `Gagal ${action}`);
    } finally {
      setBusyAct(null);
    }
  };
  const isBusy = (id, action) => busyAct === `${id}:${action}`;

  // Fase 4C: daftar WO ter-filter (dipakai desktop tabel & mobile card).
  const filteredWos = (() => {
    const q = search.trim().toLowerCase();
    return q
      ? wos.filter((w) => `${w.wo_no_offline || ""} ${w.wo_no || ""} ${w.design_name || ""}`.toLowerCase().includes(q))
      : wos;
  })();

  // Fase 4C: aksi baris WO dipakai ulang di tabel & card. sfx=suffix testid unik.
  const renderWoActions = (w, sfx = "") => (
    <>
      <Button size="sm" variant="ghost" onClick={() => openPdf(api, "work-order", w.id, w.wo_no_offline || w.wo_no)} data-testid={`v2-wo-pdf${sfx}`} title="Unduh PDF"><FileText className="w-4 h-4" /></Button>
      {w.status === "Draft" && canCreate && <Button size="sm" variant="outline" disabled={isBusy(w.id, "submit")} onClick={() => act(w.id, "submit")} data-testid={`v2-wo-submit${sfx}`}>{isBusy(w.id, "submit") && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Ajukan</Button>}
      {REVISABLE.includes(w.status) && canCreate && <Button size="sm" variant="outline" onClick={() => startRevise(w)} data-testid={`v2-wo-revise${sfx}`}>Revisi</Button>}
      {REVISABLE.includes(w.status) && canCreate && <Button size="sm" variant="ghost" className="text-destructive" disabled={isBusy(w.id, "cancel")} onClick={() => act(w.id, "cancel")} data-testid={`v2-wo-cancel${sfx}`}>{isBusy(w.id, "cancel") && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Batal</Button>}
      {w.status === "Diajukan" && canApprove && <>
        <Button size="sm" variant="outline" onClick={() => openPdf(api, "work-order", w.id, w.wo_no_offline || w.wo_no)} data-testid={`v2-wo-preview-pdf${sfx}`}>
          <FileText className="w-4 h-4 mr-1" />Preview PDF
        </Button>
        <Button size="sm" disabled={isBusy(w.id, "approve")} onClick={() => act(w.id, "approve")} data-testid={`v2-wo-approve${sfx}`}>{isBusy(w.id, "approve") && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Setujui</Button>
        <Button size="sm" variant="destructive" disabled={isBusy(w.id, "reject")} onClick={() => act(w.id, "reject")} data-testid={`v2-wo-reject${sfx}`}>{isBusy(w.id, "reject") && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Tolak</Button>
      </>}
      {w.status === "Disetujui" && canCreate &&
        <Button size="sm" disabled={isBusy(w.id, "production-done")} onClick={() => act(w.id, "production-done")} data-testid={`v2-wo-production-done${sfx}`}>
          {isBusy(w.id, "production-done") ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PackageCheck className="w-4 h-4 mr-1" />}Selesai Produksi
        </Button>}
      {w.status === "Selesai Produksi" && canCreate &&
        <Button size="sm" disabled={isBusy(w.id, "send-warehouse")} onClick={() => act(w.id, "send-warehouse")} data-testid={`v2-wo-send-warehouse${sfx}`}>
          {isBusy(w.id, "send-warehouse") ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Kirim ke Gudang
        </Button>}
      {w.status === "Dikirim ke Gudang" && <>
        <Badge variant="success" data-testid={`v2-wo-items-ready${sfx}`}>QR &amp; item dibuat</Badge>
        {canCreate &&
        <Button size="sm" variant="outline" onClick={() => openPdf(api, "wo-labels", w.id, `Label ${w.wo_no}`)} data-testid={`v2-wo-print-labels${sfx}`}>
          <QrCode className="w-4 h-4 mr-1" />Download QR (Label)
        </Button>}
      </>}
    </>
  );

  // Fase 4C: status + timeline WO (dipakai desktop & mobile).
  const renderWoStatus = (w, sfx = "") => (
    <>
      <StatusBadge status={w.status} testid="v2-wo-status" />
      <div className="text-[10px] text-muted-foreground mt-1 space-y-0.5" data-testid={`v2-wo-timeline${sfx}`}>
        {w.approved_at && <div>Disetujui: {fmtTs(w.approved_at)}</div>}
        {w.production_done_at && <div>Selesai Produksi: {fmtTs(w.production_done_at)}</div>}
        {w.sent_warehouse_at && <div>Dikirim ke Gudang: {fmtTs(w.sent_warehouse_at)}</div>}
      </div>
    </>
  );

  const woJenis = (w, sfx = "") => (
    <div className="flex flex-wrap gap-1" data-testid={`v2-wo-jenis-badge${sfx}`}>
      {(w.stock_types || [w.item_category]).map((c) => (
        <CategoryBadge key={c} cat={c} />
      ))}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="v2-workorder-page">
      <h2 className="text-2xl font-semibold">Work Order</h2>

      {canCreate && (
        <Card className="p-5 rounded-2xl">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">{editId ? "Revisi Work Order" : "Buat Work Order"}</h3>
            <Badge variant="outline" data-testid="v2-wo-prod-region">
              Region Produksi: {REGION_LABEL[prodRegion] || "-"}
            </Badge>
          </div>

          <div className="mb-4 grid sm:grid-cols-2 gap-3 max-w-2xl">
            <div>
              <Label className="text-xs">Nomor WO Offline <span className="text-destructive">*</span></Label>
              <Input value={woOffline} onChange={(e) => setWoOffline(e.target.value)} placeholder="cth: WO-OFL-001 (wajib)" data-testid="v2-wo-offline" />
            </div>
            <div>
              <Label className="text-xs">Tahun Edisi (opsional)</Label>
              <Input value={design} onChange={(e) => setDesign(e.target.value)} placeholder="cth: 2026" data-testid="v2-wo-design" />
            </div>
          </div>

          <div className="space-y-3">
            {lines.map((l, idx) => {
              const p = productById(l.product_id);
              const owner = p ? (p.owner_region || p.region) : null;
              const crossRegion = owner && prodRegion && owner !== prodRegion;
              return (
                <div key={l.key} className="rounded-xl border p-3 space-y-3" data-testid="v2-wo-line">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    <div className="md:col-span-5">
                      <Label className="text-xs">Merchandize</Label>
                      <Select value={l.product_id} onValueChange={(v) => setLine(l.key, { product_id: v, sizes: {}, qtyNoSize: "" })}>
                        <SelectTrigger data-testid="v2-wo-line-merch"><SelectValue placeholder="Pilih Merchandize..." /></SelectTrigger>
                        <SelectContent>
                          {products.map((pr) => (
                            <SelectItem key={pr.id} value={pr.id} data-testid={`v2-wo-merch-${pr.id}`}>
                              {pr.name} — {pr.club}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-xs">Region Pemilik Merchandize</Label>
                      <div className="h-9 flex items-center" data-testid="v2-wo-line-owner">
                        {owner ? <Badge variant="secondary">{REGION_LABEL[owner] || owner}</Badge>
                          : <span className="text-xs text-muted-foreground">—</span>}
                      </div>
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-xs">Jenis Stok</Label>
                      <Select value={l.item_category} onValueChange={(v) => setLine(l.key, { item_category: v })}>
                        <SelectTrigger data-testid="v2-wo-line-jenis"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Jual" data-testid="v2-wo-jenis-jual">JUAL</SelectItem>
                          <SelectItem value="MoU" data-testid="v2-wo-jenis-mou">MoU</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-1 flex justify-end">
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => removeLine(l.key)}
                        disabled={lines.length <= 1} data-testid="v2-wo-line-remove" title="Hapus baris">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {crossRegion && (
                    <div className="flex items-start gap-2 text-xs rounded-lg bg-muted px-3 py-2" data-testid="v2-wo-cross-info">
                      <Info className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>
                        Merchandize ini milik region <b>{REGION_LABEL[owner]}</b>. Setelah produksi selesai,
                        stok masuk Gudang <b>{REGION_LABEL[prodRegion]}</b> dulu dan wajib ditransfer ke Gudang
                        <b> {REGION_LABEL[owner]}</b> sebelum dikirim ke store.
                      </span>
                    </div>
                  )}

                  {l.product_id && (
                    p && !p.has_size_variants ? (
                      <div className="max-w-[200px]">
                        <Label className="text-xs">Quantity</Label>
                        <Input type="number" min="0" value={l.qtyNoSize}
                          onChange={(e) => setLine(l.key, { qtyNoSize: e.target.value })}
                          placeholder="0" data-testid="v2-wo-qty-nosize" />
                      </div>
                    ) : (
                      <div>
                        <Label className="text-xs">Size &amp; Quantity</Label>
                        <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 mt-1">
                          {SIZES.map((sz) => (
                            <div key={sz}>
                              <div className="text-[10px] text-center text-muted-foreground">{sz}</div>
                              <Input type="number" min="0" value={l.sizes[sz] || ""}
                                onChange={(e) => setSizeQty(l.key, sz, e.target.value)}
                                className="text-center px-1" placeholder="0" data-testid={`v2-wo-size-${sz}`} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  )}

                  <div className="text-xs text-muted-foreground">Total baris #{idx + 1}: <b>{lineTotal(l)}</b> pcs</div>
                </div>
              );
            })}
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setLines((ls) => [...ls, newLine()])} data-testid="v2-wo-add-line">
              <Plus className="w-4 h-4 mr-1" />Tambah Merchandize
            </Button>
            <div className="flex-1" />
            <Button onClick={submitForm} disabled={submitting} data-testid="v2-wo-create">
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PackageCheck className="w-4 h-4 mr-1" />}
              {submitting ? "Menyimpan…" : (editId ? "Simpan Revisi" : "Buat Work Order")}
            </Button>
            {editId && <Button variant="ghost" onClick={resetForm} data-testid="v2-wo-cancel-edit">Batal Edit</Button>}
          </div>
        </Card>
      )}

      <Card className="p-5 rounded-2xl" ref={listTopRef}>
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h3 className="font-semibold">Daftar Work Order</h3>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari Nomor WO Offline / sistem..."
              className="h-8 w-full sm:w-56" data-testid="v2-wo-search" />
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          </div>
        </div>
        <div className="hidden md:block overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Nomor WO</TableHead><TableHead>Club</TableHead><TableHead>Jenis Stok</TableHead>
              <TableHead>Region Produksi</TableHead><TableHead>Qty</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filteredWos.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">{search.trim() ? "Tidak ada hasil." : "Belum ada Work Order."}</TableCell></TableRow>
              )}
              {filteredWos.map((w) => (
                <TableRow key={w.id} data-testid="v2-wo-row">
                  <TableCell data-testid="v2-wo-no">
                    <div className="font-medium text-sm">{w.wo_no_offline || w.display_no || w.wo_no}</div>
                    {w.wo_no_offline
                      ? <div className="font-mono text-[10px] text-muted-foreground">sys: {w.wo_no}</div>
                      : <div className="text-[10px] text-[hsl(var(--warning))]">No. WO Offline belum diisi</div>}
                    {w.design_name && <div className="text-[10px] text-muted-foreground">{w.design_name}</div>}
                  </TableCell>
                  <TableCell>{w.club}</TableCell>
                  <TableCell>{woJenis(w)}</TableCell>
                  <TableCell>{REGION_LABEL[w.production_region] || "-"}</TableCell>
                  <TableCell>{w.total_qty}</TableCell>
                  <TableCell>{renderWoStatus(w)}</TableCell>
                  <TableCell className="text-right space-x-1">{renderWoActions(w)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile: card list Work Order */}
        <div className="md:hidden space-y-3" data-testid="v2-wo-cards">
          {filteredWos.length === 0 && (
            <div className="text-center text-muted-foreground py-6 text-sm rounded-xl border border-dashed">{search.trim() ? "Tidak ada hasil." : "Belum ada Work Order."}</div>
          )}
          {filteredWos.map((w) => (
            <div key={w.id} className="rounded-xl border p-3 space-y-2" data-testid="v2-wo-row-m">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0" data-testid="v2-wo-no-m">
                  <div className="font-medium text-sm truncate">{w.wo_no_offline || w.display_no || w.wo_no}</div>
                  {w.wo_no_offline
                    ? <div className="font-mono text-[10px] text-muted-foreground">sys: {w.wo_no}</div>
                    : <div className="text-[10px] text-[hsl(var(--warning))]">No. WO Offline belum diisi</div>}
                  {w.design_name && <div className="text-[10px] text-muted-foreground">{w.design_name}</div>}
                </div>
                {woJenis(w, "-m")}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>Club: <b className="text-foreground">{w.club}</b></span>
                <span>Region: <b className="text-foreground">{REGION_LABEL[w.production_region] || "-"}</b></span>
                <span>Qty: <b className="text-foreground">{w.total_qty}</b></span>
              </div>
              <div>{renderWoStatus(w, "-m")}</div>
              <div className="flex flex-wrap gap-2 pt-1 border-t">{renderWoActions(w, "-m")}</div>
            </div>
          ))}
        </div>
        <ListPager
          page={page} pageSize={pageSize} total={meta.total} pages={meta.pages} loading={loading}
          onPageChange={goPage} onPageSizeChange={changePageSize}
          storageKey="work-orders" testid="wo-pager"
        />
      </Card>
    </div>
  );
}
